import { motion } from "framer-motion";
import {
  ShieldCheck,
  Landmark,
  FileText,
  ExternalLink,
  Lock,
  Info,
  AlertTriangle,
} from "lucide-react";
import ScreenHeader from "../components/ScreenHeader";
import PartnerBadge from "../components/PartnerBadge";
import { useApp } from "../context/AppContext";

export const LegalScreen = () => {
  const { partner } = useApp();

  return (
    <motion.div
      initial={{ x: 20, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: -20, opacity: 0 }}
      transition={{ type: "spring", bounce: 0, duration: 0.3 }}
      className="h-full flex flex-col"
      data-testid="legal-screen"
    >
      <ScreenHeader title="Legal & compliance" testId="legal-back-btn" />

      <div className="flex-1 overflow-y-auto phone-scroll px-6 pb-10">
        <h1 className="font-display text-[26px] font-bold tracking-tight">
          Legal &amp; compliance
        </h1>
        <p className="text-[13px] text-gray-500 mt-1.5 leading-snug">
          How Shwiip works, what it is, and — importantly — what it isn't.
        </p>

        {/* Hero compliance card */}
        <div className="mt-5 rounded-3xl bg-gray-900 text-white p-5">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-gray-400 font-bold">
            <ShieldCheck className="w-3.5 h-3.5" strokeWidth={2.5} />
            Regulatory posture
          </div>
          <div className="font-display text-xl font-bold mt-2 leading-tight">
            Shwiip is a technical interface — not a financial institution.
          </div>
          <div className="text-[12px] text-gray-300 mt-3 leading-snug">
            All payment execution, fund custody and settlement are performed by
            licensed partners. Shwiip never holds, routes or moves your money.
          </div>
        </div>

        {/* What Shwiip is / is NOT */}
        <div className="mt-5 grid grid-cols-1 gap-3">
          <Card
            tone="emerald"
            icon={ShieldCheck}
            title="Shwiip IS"
            items={[
              "A Technical Service Provider (TSP)",
              "A payment orchestration layer",
              "A routing interface to licensed partners",
              "A user experience layer",
            ]}
          />
          <Card
            tone="red"
            icon={AlertTriangle}
            title="Shwiip is NOT"
            items={[
              "A bank",
              "An Electronic Money Institution (EMI)",
              "A wallet provider",
              "A custodian of your funds",
            ]}
          />
        </div>

        {/* Partner */}
        <div className="mt-5">
          <div className="text-[11px] uppercase tracking-widest font-bold text-gray-400 mb-2">
            Payment partner
          </div>
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-white border border-slate-200 flex items-center justify-center flex-shrink-0">
                <Landmark className="w-4.5 h-4.5 text-slate-700" strokeWidth={2.2} />
              </div>
              <div className="min-w-0">
                <div className="font-semibold text-[14px] text-slate-900">
                  {partner.name}
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5">
                  {partner.license}
                </div>
                <div className="text-[11px] text-slate-500 mt-2 leading-snug">
                  Finshark is an Account Information Service Provider (AISP) and
                  Payment Initiation Service Provider (PISP) authorised under PSD2.
                  All payment instructions initiated through Shwiip are executed
                  directly by Finshark.
                </div>
              </div>
            </div>
          </div>
          <PartnerBadge partner={partner.name} variant="inline" className="mt-3" />
        </div>

        {/* Transaction lifecycle */}
        <div className="mt-6">
          <div className="text-[11px] uppercase tracking-widest font-bold text-gray-400 mb-2">
            Transaction lifecycle
          </div>
          <div className="rounded-2xl border border-gray-100 bg-gray-50 p-4 space-y-2">
            {[
              ["INIT", "User submits a payment request"],
              ["VALIDATED", "Shwiip validates recipient & limits"],
              ["ROUTED", `Request is routed to ${partner.name}`],
              ["AUTH_PENDING", "User approves via BankID / biometric"],
              ["PROCESSING", `${partner.name} executes the payment`],
              ["COMPLETED / FAILED", "Outcome is recorded and displayed"],
            ].map(([state, desc]) => (
              <div key={state} className="flex items-start gap-3">
                <span className="inline-block text-[9px] font-bold tracking-wider bg-white border border-gray-200 rounded-full px-2 py-1 min-w-[90px] text-center">
                  {state}
                </span>
                <span className="text-[12px] text-gray-600 leading-snug pt-1">
                  {desc}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Policies */}
        <div className="mt-6">
          <div className="text-[11px] uppercase tracking-widest font-bold text-gray-400 mb-2">
            Policies
          </div>
          <div className="rounded-2xl border border-gray-100 overflow-hidden">
            <PolicyLink label="Terms of Service" testId="legal-tos" />
            <PolicyLink label="Privacy Policy" testId="legal-privacy" />
            <PolicyLink label="Cookie Policy" testId="legal-cookies" />
            <PolicyLink
              label="Partner Disclosures (Finshark)"
              testId="legal-partner-disclosures"
              last
            />
          </div>
        </div>

        <div className="mt-5 flex items-start gap-2 text-[11px] text-gray-500 leading-snug">
          <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" strokeWidth={2.2} />
          <span>
            This screen summarises Shwiip's regulatory posture. It is for
            informational purposes and does not replace the full legal documents.
          </span>
        </div>

        <div className="mt-6 text-center text-[10px] text-gray-400">
          Shwiip Technologies · v0.1 · Technical interface only
        </div>
      </div>
    </motion.div>
  );
};

const Card = ({ tone, icon: Icon, title, items }) => {
  const cls = {
    emerald: "border-emerald-100 bg-emerald-50/40",
    red: "border-red-100 bg-red-50/40",
  }[tone];
  const iconCls = {
    emerald: "bg-emerald-100 text-emerald-700",
    red: "bg-red-100 text-red-600",
  }[tone];
  return (
    <div className={"rounded-2xl border p-4 " + cls}>
      <div className="flex items-center gap-2">
        <div
          className={
            "w-7 h-7 rounded-full flex items-center justify-center " + iconCls
          }
        >
          <Icon className="w-3.5 h-3.5" strokeWidth={2.5} />
        </div>
        <div className="font-bold text-[13px] text-gray-900">{title}</div>
      </div>
      <ul className="mt-2 space-y-1.5">
        {items.map((it) => (
          <li key={it} className="text-[12px] text-gray-700 flex items-start gap-2">
            <span className="text-gray-400 mt-[1px]">›</span>
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const PolicyLink = ({ label, testId, last }) => (
  <button
    data-testid={testId}
    className={
      "w-full flex items-center justify-between px-4 py-3.5 text-left hover:bg-gray-50 transition-colors " +
      (last ? "" : "border-b border-gray-100")
    }
  >
    <div className="flex items-center gap-3">
      <FileText className="w-4 h-4 text-gray-500" strokeWidth={2.2} />
      <span className="text-[13px] font-semibold text-gray-900">{label}</span>
    </div>
    <ExternalLink className="w-3.5 h-3.5 text-gray-400" strokeWidth={2.2} />
  </button>
);

export default LegalScreen;
